<template>
  <div
    id="base-list-filter"
    @keydown.enter="applyFilters"
    class="mb-3"
  >
    <b-form @submit.prevent>
      <b-row no-gutters>
        <b-col
          cols="12"
          md="12"
          lg="8"
          xl="5"
        >
          <!-- @slot Content in this slot will be inline with the button to open the filter modal. -->
          <slot name="quick-filters" />
        </b-col>

        <b-col
          cols="auto"
          class="ml-2 mr-auto"
        >
          <b-button
            v-if="advancedFilter"
            id="advanced-filter-button"
            v-b-modal.advanced-filter-modal
            v-b-tooltip.hover
            class="text-primary"
            variant="link"
            :title="title"
          >
            <i class="fas fa-sliders-h mr-2" />All Filters
            <number-badge
              v-show="totalAppliedFilters.length > 0"
              class="d-inline"
              variant="primary"
              :total="totalAppliedFilters"
            />
          </b-button>

          <b-modal
            :title="title"
            v-if="advancedFilter"
            id="advanced-filter-modal"
            size="xl"
            hide-footer
            no-close-on-backdrop
            static
          >
            <template #modal-header-close>
              <i class="fa fa-close" />
            </template>
            <slot name="advanced-filters" />

            <hr class="mt-4">

            <b-row>
              <b-col
                offset-lg="6"
                lg="3"
                order="2"
                order-lg="1"
              >
                <b-button
                  id="advanced-filter-reset-button"
                  variant="link-dark"
                  block
                  @click="onFiltersReset"
                >
                  Clear All
                </b-button>
              </b-col>
              <b-col
                lg="3"
                order="1"
                order-lg="2"
              >
                <b-button
                  id="advanced-filter-apply-button"
                  variant="primary"
                  block
                  @click="applyFilters"
                >
                  Apply Filters
                </b-button>
              </b-col>
            </b-row>
          </b-modal>

          <b-button
            v-if="popoverFilter"
            id="dropdown-filter-button"
            variant="link"
            :title="title"
            v-b-tooltip.hover
            class="text-primary"
            href="#"
            tabindex="0"
            @click="isMobile ? $bvModal.show('mobile-popover-filter-modal') : showPopoverFilters = true"
          >
            <i class="fas fa-sliders-h mr-2" />All Filters
            <number-badge
              v-show="totalAppliedFilters.length > 0"
              class="d-inline"
              variant="primary"
              :total="totalAppliedFilters"
            />
          </b-button>

          <b-popover
            v-if="popoverFilter && !isMobile"
            triggers="focus"
            :show.sync="showPopoverFilters"
            custom-class="popover-filters p-3"
            target="dropdown-filter-button"
            placement="bottomleft"
            container="base-list-filter"
          >
            <!-- @slot Content in this slot will be contained within the modal. -->
            <slot name="popover-filters" />

            <b-button
              id="popover-filter-apply-button"
              block
              variant="primary"
              @click="applyPopoverFilters"
            >
              Filter
            </b-button>
          </b-popover>

          <b-modal
            id="mobile-popover-filter-modal"
            v-if="popoverFilter && isMobile"
            title="All Filters"
            size="lg"
            hide-footer
            no-close-on-backdrop
            static
          >
            <slot name="popover-filters" />
            <b-row>
              <b-col
                offset-lg="6"
                lg="3"
                order="2"
                order-lg="1"
              >
                <b-button
                  id="advanced-filter-reset-button"
                  variant="link-dark"
                  block
                  @click="onFiltersReset"
                >
                  Clear All
                </b-button>
              </b-col>
              <b-col
                lg="3"
                order="1"
                order-lg="2"
              >
                <b-button
                  id="advanced-filter-apply-button"
                  variant="primary"
                  block
                  @click="applyPopoverFilters"
                >
                  Apply Filters
                </b-button>
              </b-col>
            </b-row>
          </b-modal>
        </b-col>

        <b-col cols="auto">
          <b-button
            v-if="columnPicker"
            class="mb-3"
            id="column-picker-button"
            v-b-tooltip.hover
            v-b-modal.column-picker-modal
            variant="outline-secondary"
            title="Change table columns"
          >
            <i class="fa fa-columns" /> Columns
          </b-button>
          <b-modal
            :title="columnsTitle"
            v-if="columnPicker"
            id="column-picker-modal"
            size="xl"
            hide-footer
            no-close-on-backdrop
            static
            @show="onColumnPickerOpen"
          >
            <template #modal-header-close>
              <i class="fa fa-close" />
            </template>
            <b-row>
              <b-col cols="12">
                <p class="text-secondary mb-3">
                  Check columns to show them in the table. Drag to reorder. The order defines the column order in the table.
                </p>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <div
                  id="column-picker-columns"
                  class="column-picker-list"
                >
                  <draggable
                    v-model="columnList"
                    handle=".grab-handle"
                    ghost-class="column-picker-ghost"
                    tag="div"
                    class="column-picker-draggable"
                  >
                    <div
                      v-for="item in columnList"
                      :key="item.column.key"
                      class="column-picker-item d-flex align-items-center py-2 px-3 border-bottom"
                    >
                      <i class="fa fa-grip-vertical grab-handle text-secondary mr-2" />
                      <b-form-checkbox
                        :checked="item.selected"
                        class="mb-0 mr-2"
                        @input="(checked) => onColumnToggle(item, checked)"
                      />
                      <span class="flex-grow-1">{{ item.column.label }}</span>
                    </div>
                  </draggable>
                </div>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-link
                  id="column-picker-reset-button"
                  href="#"
                  class="d-inline-block mt-2 text-primary font-weight-medium"
                  @click.prevent="resetToDefaultColumns"
                >
                  <i class="fa fa-undo mr-2" />Reset to Default
                </b-link>
                <h4 class="mb-0 mt-3">
                  Preview
                </h4>
                <p class="text-secondary">
                  Columns will appear in the table in the order shown above (checked items only).
                </p>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-table-simple
                  responsive
                  class="mb-0"
                >
                  <b-thead>
                    <b-tr>
                      <b-th
                        v-for="col in selectedColumnsFromList"
                        :key="col.key"
                      >
                        {{ col.label }}
                      </b-th>
                    </b-tr>
                  </b-thead>
                </b-table-simple>
              </b-col>
            </b-row>

            <hr class="mt-4">

            <b-row>
              <b-col
                offset-lg="9"
                lg="3"
              >
                <b-button
                  id="column-picker-apply-button"
                  block
                  variant="primary"
                  @click="applyColumnFilters"
                >
                  Save
                </b-button>
              </b-col>
            </b-row>
          </b-modal>
          <slot name="right-tray" />
        </b-col>
      </b-row>

      <slot name="overflow" />
      <b-row no-gutters>
        <b-col
          v-for="pill in filters"
          :key="pill.key"
          cols="auto"
          class="mb-1"
        >
          <b-button
            variant="light"
            size="sm"
            class="pill-filter mr-2"
            @click="clearFilter(pill.key)"
          >
            {{ pill.label }}: {{ pill.value }}
            <i class="fa fa-times" />
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </div>
</template>

<script>
import { reduce as _reduce, uniq as _uniq } from 'lodash';
import draggable from 'vuedraggable';
import NumberBadge from '@/components/shared/NumberBadge.vue';

export default {
  name: 'BaseListFilter',
  components: {
    NumberBadge,
    draggable,
  },
  props: {
    title: {
      type: String,
      default: 'Filters',
    },
    tableName: {
      type: String,
      required: true,
    },
    columnsTitle: {
      type: String,
      default: 'Manage Columns',
    },
    /**
     * The filters that are currently applied to the list.
     */
    appliedFilters: {
      type: Object,
      default: () => new Object(),
    },
    /**
     * The columns that the list is currently displaying.
     */
    displayedColumns: {
      type: Array,
      default: () => [],
    },
    /**
     * The columns that are available to be displayed.
     */
    availableColumns: {
      type: Array,
      default: () => [],
    },
    /**
     * Whether or not to render the column picker.
     */
    columnPicker: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether or not to render the advanced filters.
     */
    advancedFilter: {
      type: Boolean,
      default: true,
    },
    /**
     * Whether or not to render the dropdown filters.
     */
    popoverFilter: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedColumns: this.displayedColumns,
      columnList: [],
      showPopoverFilters: false,
      isMobile: window.innerWidth < 768
    };
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    filters() {
      return _reduce(
        this.appliedFilters,
        (applied, filter, key) => {
          if (this.findOptionColumnByKey(key) && filter) {
            applied.push({
              value: filter,
              ...this.findOptionColumnByKey(key),
            });
          }
          return applied;
        },
        []
      );
    },
    totalAppliedFilters() {
      return Object.values(this.filters).length;
    },
    pickerColumns() {
      return this.availableColumns.filter((col) => !col.filterOnly);
    },
    selectedColumnsFromList() {
      return (this.columnList || []).filter((item) => item.selected).map((item) => item.column);
    },
    defaultColumns() {
      return this.$store.getters.defaultColumnsForTable(this.tableName) || [];
    },
  },
  watch: {
    appliedFilters() {
      this.closeModals();
    },
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },
    /**
     * Triggers when the "Apply Filter" button is clicked.
     *
     * @property {array} selectedColumns The columns currently selected from the column.
     */
    applyFilters() {
      this.$store.dispatch('updateProfile', { ui_config: { listColumns: { ...this.$store.getters.userListColumns, [this.tableName]: _uniq(this.selectedColumns.map((column) => column.key)) } } });
      this.$emit('filters-applied', this.selectedColumns);
    },
    /**
     * Triggers when the "Reset" button is clicked.
     */
    onFiltersReset() {
      this.$emit('filters-reset');
    },
    /**
     * Triggers when one of the filter pills is clicked.
     *
     * @property {string} key The identified of the filter which has been clicked. This is the key property from the appliedFilters prop.
     */
    clearFilter(key) {
      this.$emit('filter-cleared', key);
    },
    findOptionColumnByKey(key) {
      return this.availableColumns.find((column) => column.key == key);
    },
    closeModals() {
      this.$bvModal.hide('advanced-filter-modal');
      this.$bvModal.hide('column-picker-modal');
    },
    applyPopoverFilters() {
      this.applyFilters();

      if (this.isMobile) {
        this.$bvModal.hide('mobile-popover-filter-modal');
      } else {
        this.showPopoverFilters = false;
      }
    },
    onColumnPickerOpen() {
      this.columnList = this.buildColumnList();
    },
    buildColumnList(fromColumns) {
      const columns = fromColumns ?? this.displayedColumns ?? [];
      const displayedKeys = new Set(columns.map((col) => (typeof col === 'object' ? col.key : col)));
      const selected = columns.map((col) => {
        const key = typeof col === 'object' ? col.key : col;
        const fullCol = this.pickerColumns.find((c) => c.key === key) || (typeof col === 'object' ? col : { key, label: key });
        return { column: fullCol, selected: true };
      });
      const unselected = this.pickerColumns
        .filter((col) => !displayedKeys.has(col.key))
        .map((col) => ({ column: col, selected: false }));
      return [...selected, ...unselected];
    },
    onColumnToggle(item, checked) {
      item.selected = checked;
      this.reorderColumnListAfterToggle();
    },
    reorderColumnListAfterToggle() {
      const selected = this.columnList.filter((i) => i.selected);
      const unselected = this.columnList.filter((i) => !i.selected);
      this.columnList = [...selected, ...unselected];
    },
    applyColumnFilters() {
      this.selectedColumns = this.selectedColumnsFromList;
      this.applyFilters();
      this.$bvModal.hide('column-picker-modal');
    },
    resetToDefaultColumns() {
      const defaults = this.defaultColumns;
      if (defaults.length > 0) {
        this.columnList = this.buildColumnList(defaults);
      }
    },
  },
};
</script>

<style scoped>
.popover-filters {
  font-size: 1rem;
  min-width: 576px;
}
@media (max-width: 768px) {
  .popover-filters {
    min-width: 100% !important;
    max-width: 100vw;
  }
}
.column-picker-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}
.column-picker-ghost {
  opacity: 0.5;
  background: #f8f9fa;
}
</style>
